'use client';

/**
 * SplitPanelHeader Component
 *
 * Header section for SplitPanel with title, subtitle, close button, and optional actions.
 */

import CloseIcon from '@mui/icons-material/Close';
import { alpha, Box, IconButton, type SxProps, type Theme, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

// ============================================================================
// Types
// ============================================================================

export interface SplitPanelHeaderProps {
  /** Panel title */
  title?: React.ReactNode;
  /** Subtitle or description */
  subtitle?: React.ReactNode;
  /** Optional icon rendered before the title */
  icon?: React.ReactNode;
  /**
   * Optional action rendered on the LEFT side of the header — typically a
   * back button for multi-step flows (e.g. subtype picker → upload form).
   */
  leftAction?: React.ReactNode;
  /** Close button handler */
  onClose: () => void;
  /**
   * Visibly disable the close button (greyed out, click no-ops).
   * Use this when the panel is locked mid-mutation — preferable to
   * passing a silent no-op `onClose`, which leaves the X looking
   * clickable but unresponsive (worst-of-both UX).
   */
  closeDisabled?: boolean;
  /** Additional action buttons */
  actions?: React.ReactNode;
  /** @deprecated Use `actions`. Kept for backward compatibility. */
  action?: React.ReactNode;
  /** Optional sx overrides applied to the outer header container. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Styled Components
// ============================================================================

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
  flexShrink: 0,
}));

const LeftActionSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginRight: theme.spacing(0.5),
  flexShrink: 0,
}));

const TitleSection = styled(Box)({
  flex: 1,
  minWidth: 0,
});

const ActionsSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginLeft: theme.spacing(1),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.75),
  color: theme.palette.text.secondary,
  transition: 'all 150ms ease',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.1) : alpha(theme.palette.common.black, 0.06),
    color: theme.palette.text.primary,
  },
}));

// ============================================================================
// Component
// ============================================================================

export const SplitPanelHeader: React.FC<SplitPanelHeaderProps> = ({
  title,
  subtitle,
  icon,
  leftAction,
  onClose,
  closeDisabled,
  actions,
  action,
  sx,
}) => {
  const resolvedActions = actions ?? action;
  return (
    <HeaderContainer sx={sx}>
      {leftAction && <LeftActionSection>{leftAction}</LeftActionSection>}
      <TitleSection>
        {title && (
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 600,
              fontSize: '1.1rem',
              lineHeight: 1.3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {icon}
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography
            variant="body2"
            component="div"
            sx={{
              color: 'text.secondary',
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </TitleSection>
      <ActionsSection>
        {resolvedActions}
        <CloseButton onClick={onClose} disabled={closeDisabled} aria-label="Close panel" size="small">
          <CloseIcon fontSize="small" />
        </CloseButton>
      </ActionsSection>
    </HeaderContainer>
  );
};

export default SplitPanelHeader;
